import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Messages } from 'primereact/messages';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { AutoComplete } from 'primereact/autocomplete';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useAxios from '../../hooks/useAxios';

const sexOptions = [
  { label: 'Male', value: 'М' },
  { label: 'Female', value: 'Ж' },
];

const PersonAdd = () => {
  const { user } = useAuth();
  const { fetchData: fetchStaffList, response: staffResponse } = useAxios();
  const { fetchData: createStaffData, response: createResponse, error: createError } = useAxios();
  const navigate = useNavigate();
  const messages = useRef(null);
  const fileUploadRef = useRef(null);
  
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    middlename: '',
    sex: '',
    email: '',
    mobile: '',
    department: '',
    division: '',
    position: '',
    vpn: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Autocomplete suggestions
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Fetch existing staff data for autocomplete suggestions
  useEffect(() => {
    fetchStaffList({ method: 'get', url: '/api/staff' });
  }, []);

  // Handle staff response for autocomplete
  useEffect(() => {
    if (staffResponse) {
      const staff = staffResponse.data || [];
      const uniqueDepts = [...new Set(staff.map(s => s.department).filter(Boolean))];
      const uniqueDivs = [...new Set(staff.map(s => s.division).filter(Boolean))];
      const uniquePos = [...new Set(staff.map(s => s.position).filter(Boolean))];
      setDepartments(uniqueDepts.sort());
      setDivisions(uniqueDivs.sort());
      setPositions(uniquePos.sort());
    }
  }, [staffResponse]);

  // AutoComplete filter methods
  const searchDepartment = (event) => {
    const query = event.query.toLowerCase();
    const filtered = departments.filter(dept => dept.toLowerCase().includes(query));
    setFilteredDepartments(filtered.length > 0 ? filtered : [event.query]);
  };

  const searchDivision = (event) => {
    const query = event.query.toLowerCase();
    const filtered = divisions.filter(div => div.toLowerCase().includes(query));
    setFilteredDivisions(filtered.length > 0 ? filtered : [event.query]);
  };

  const searchPosition = (event) => {
    const query = event.query.toLowerCase();
    const filtered = positions.filter(pos => pos.toLowerCase().includes(query));
    setFilteredPositions(filtered.length > 0 ? filtered : [event.query]);
  };

  const handleImageSelect = (e) => {
    const file = e.files && e.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      messages.current?.show({ severity: 'success', summary: 'Image Selected', detail: file.name, life: 3000 });
    }
  };

  const handleImageClear = () => {
    setImageFile(null);
    setPreviewUrl(null);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      data.append(key, value || '');
    });
    if (imageFile) {
      data.append('image', imageFile);
    }

    await createStaffData({ method: 'post', url: '/api/staff', data: data, headers: { 'Content-Type': 'multipart/form-data' } });
  };

  // Handle create response
  useEffect(() => {
    if (createResponse) {
      messages.current?.show({ severity: 'success', summary: 'Success', detail: 'Employee created successfully', life: 3000 });
      setSubmitting(false);
      setTimeout(() => {
        setForm({
          firstname: '',
          lastname: '',
          middlename: '',
          sex: '',
          email: '',
          mobile: '',
          department: '',
          division: '',
          position: '',
          vpn: '',
        });
        handleImageClear();
      }, 1000);
    }
  }, [createResponse]);

  // Handle create error
  useEffect(() => {
    if (createError) {
      messages.current?.show({ severity: 'error', summary: 'Error', detail: createError.message || 'Error creating staff member', life: 5000 });
      setSubmitting(false);
    }
  }, [createError]);

  return (
    <div className="surface-ground min-h-screen py-5">
      <div className="max-w-6xl mx-auto px-4">
        <Messages ref={messages} />
        
        {/* Header */}
        <div className="flex align-items-center justify-content-between mb-4">
          <div>
            <h1 className="text-4xl font-bold m-0 mb-2 text-900">Add New Employee</h1>
            <p className="text-600 m-0">Fill in the details below to add a new staff member to the directory</p>
          </div>
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            className="p-button-text"
            onClick={() => navigate('/staff')}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid">
            {/* Left Column - Personal Information */}
            <div className="col-12 lg:col-8">
              <Card className="mb-4">
                <h2 className="text-2xl font-semibold m-0 mb-4 text-900">
                  <i className="pi pi-user mr-2 text-primary"></i>
                  Personal Information
                </h2>
                <Divider />
                
                <div className="grid">
                  <div className="col-12 md:col-4">
                    <div className="field">
                      <label htmlFor="firstname" className="block font-medium mb-2 text-900">First Name *</label>
                      <InputText
                        id="firstname"
                        value={form.firstname}
                        onChange={(e) => handleChange('firstname', e.target.value)}
                        className="w-full"
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4">
                    <div className="field">
                      <label htmlFor="middlename" className="block font-medium mb-2 text-900">Middle Name</label>
                      <InputText
                        id="middlename"
                        value={form.middlename}
                        onChange={(e) => handleChange('middlename', e.target.value)}
                        className="w-full"
                        placeholder="Enter middle name"
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4">
                    <div className="field">
                      <label htmlFor="lastname" className="block font-medium mb-2 text-900">Last Name *</label>
                      <InputText
                        id="lastname"
                        value={form.lastname}
                        onChange={(e) => handleChange('lastname', e.target.value)}
                        className="w-full"
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4">
                    <div className="field">
                      <label htmlFor="sex" className="block font-medium mb-2 text-900">Gender</label>
                      <Dropdown
                        id="sex"
                        value={form.sex}
                        options={sexOptions}
                        onChange={(e) => handleChange('sex', e.value)}
                        placeholder="Select gender"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4">
                    <div className="field">
                      <label htmlFor="email" className="block font-medium mb-2 text-900">
                        <i className="pi pi-envelope mr-2"></i>Email *
                      </label>
                      <InputText
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="w-full"
                        placeholder="email@company.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4">
                    <div className="field">
                      <label htmlFor="mobile" className="block font-medium mb-2 text-900">
                        <i className="pi pi-phone mr-2"></i>Mobile
                      </label>
                      <InputText
                        id="mobile"
                        value={form.mobile}
                        onChange={(e) => handleChange('mobile', e.target.value)}
                        className="w-full"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Work Information */}
              <Card>
                <h2 className="text-2xl font-semibold m-0 mb-4 text-900">
                  <i className="pi pi-briefcase mr-2 text-primary"></i>
                  Work Information
                </h2>
                <Divider />
                
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label htmlFor="department" className="block font-medium mb-2 text-900">
                        <i className="pi pi-building mr-2"></i>Department
                      </label>
                      <AutoComplete
                        id="department"
                        value={form.department}
                        suggestions={filteredDepartments}
                        completeMethod={searchDepartment}
                        onChange={(e) => handleChange('department', e.value)}
                        placeholder="Select or type department"
                        className="w-full"
                        inputClassName="w-full"
                        dropdown
                        forceSelection={false}
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label htmlFor="division" className="block font-medium mb-2 text-900">
                        <i className="pi pi-sitemap mr-2"></i>Division
                      </label>
                      <AutoComplete
                        id="division"
                        value={form.division}
                        suggestions={filteredDivisions}
                        completeMethod={searchDivision}
                        onChange={(e) => handleChange('division', e.value)}
                        placeholder="Select or type division"
                        className="w-full"
                        inputClassName="w-full"
                        dropdown
                        forceSelection={false}
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label htmlFor="position" className="block font-medium mb-2 text-900">
                        <i className="pi pi-id-card mr-2"></i>Position
                      </label>
                      <AutoComplete
                        id="position"
                        value={form.position}
                        suggestions={filteredPositions}
                        completeMethod={searchPosition}
                        onChange={(e) => handleChange('position', e.value)}
                        placeholder="Select or type position"
                        className="w-full"
                        inputClassName="w-full"
                        dropdown
                        forceSelection={false}
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label htmlFor="vpn" className="block font-medium mb-2 text-900">
                        <i className="pi pi-shield mr-2"></i>VPN Access
                      </label>
                      <InputText
                        id="vpn"
                        value={form.vpn}
                        onChange={(e) => handleChange('vpn', e.target.value)}
                        className="w-full"
                        placeholder="VPN credentials"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column - Photo Upload */}
            <div className="col-12 lg:col-4">
              <Card className="sticky" style={{ top: '2rem' }}>
                <h2 className="text-2xl font-semibold m-0 mb-4 text-900">
                  <i className="pi pi-image mr-2 text-primary"></i>
                  Employee Photo
                </h2>
                <Divider />
                
                <div className="flex flex-column align-items-center">
                  {previewUrl ? (
                    <div className="relative mb-4">
                      <Avatar
                        image={previewUrl}
                        size="xlarge"
                        shape="circle"
                        style={{ width: '200px', height: '200px' }}
                        className="shadow-4"
                      />
                      <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-danger p-button-sm"
                        style={{ position: 'absolute', top: '0', right: '0' }}
                        onClick={handleImageClear}
                        type="button"
                        tooltip="Remove image"
                      />
                    </div>
                  ) : (
                    <Avatar
                      icon="pi pi-user"
                      size="xlarge"
                      shape="circle"
                      style={{ width: '200px', height: '200px', backgroundColor: '#e9ecef', color: '#6c757d' }}
                      className="shadow-2 mb-4"
                    />
                  )}

                  <FileUpload
                    ref={fileUploadRef}
                    mode="basic"
                    accept="image/*"
                    maxFileSize={5000000}
                    chooseLabel={previewUrl ? "Change Photo" : "Upload Photo"}
                    className="w-full"
                    auto
                    customUpload
                    uploadHandler={handleImageSelect}
                    chooseOptions={{
                      icon: 'pi pi-upload',
                      className: 'w-full p-button-outlined'
                    }}
                  />
                  
                  <small className="text-600 mt-3 text-center">
                    Recommended: Square image, max 5MB
                    <br />
                    (JPG, PNG, GIF)
                  </small>
                </div>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined p-button-secondary"
              onClick={() => navigate('/staff')}
              disabled={submitting}
            />
            <Button
              type="submit"
              label={submitting ? 'Creating...' : 'Create Employee'}
              icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              disabled={submitting}
              className="px-5"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonAdd;
